import { getDecodedUrlToDisplay } from '@carboncopyco/editor-utils';
import { RuleApplyOnType } from '@carboncopyco/types';
import { Checkbox, TextBase } from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import { isEqual } from 'lodash-es';
import { useContext, useState } from 'react';
import { ApplyOnDropdown } from '../../ApplyOnDropdown';
import { InputField } from '../../InputField';
import { TextReplaceFormContext } from '../TextReplaceFormContext';
import { textReplaceDefaultValues } from '../defaultValues';
import { isRuleApplyToAllPages, updateApplyRuleOnUrlList } from '../utils';
import { AdvancedSettingsToggleButton } from './AdvancedSettingsToggleButton';
import { TextReplaceAdvancedSettingsTooltip } from './TextReplaceAdvancedSettingsTooltip';

export type TextReplaceAdvancedSettingsProps = {
  location?: {
    decodedUrl: string;
  };
};

export const TextReplaceAdvancedSettings = ({
  location,
}: TextReplaceAdvancedSettingsProps) => {
  const { advancedSettings, setAdvancedSettings } = useContext(
    TextReplaceFormContext
  );
  const [isAdvancedSettingVisible, setIsAdvancedSettingVisible] = useBoolean(
    !isEqual(textReplaceDefaultValues.options, advancedSettings)
  );
  const [applyOn, setApplyOn] = useState<RuleApplyOnType>(
    isRuleApplyToAllPages(advancedSettings.applyOnUrls)
      ? RuleApplyOnType.allPages
      : RuleApplyOnType.currentPage
  );
  const decodedUrl = getDecodedUrlToDisplay(location.decodedUrl ?? '');

  return (
    <div className="pt-2">
      <AdvancedSettingsToggleButton
        isAdvancedSettingVisible={isAdvancedSettingVisible}
        toggle={setIsAdvancedSettingVisible.toggle}
      />
      {isAdvancedSettingVisible && (
        <div className="flex flex-col gap-1.5 pt-4">
          <InputField
            label="General"
            name="general"
          >
            <div className="flex flex-col gap-1">
              <Checkbox
                label={
                  <div className="flex">
                    <TextBase
                      label="Match whole words"
                      color="text-ds-natural-600"
                    />
                    <TextReplaceAdvancedSettingsTooltip message="When on, will avoid partial matches" />
                  </div>
                }
                name="exactMatch"
                checked={advancedSettings.isExactWord}
                onChange={() =>
                  setAdvancedSettings({
                    ...advancedSettings,
                    isExactWord: !advancedSettings.isExactWord,
                  })
                }
              />
              <Checkbox
                label={
                  <div className="flex">
                    <TextBase
                      label="Match case"
                      color="text-ds-natural-600"
                    />
                    <TextReplaceAdvancedSettingsTooltip message="When on, the search will be case-sensitive" />
                  </div>
                }
                name="caseSensitive"
                checked={advancedSettings.isCaseSensitive}
                onChange={() =>
                  setAdvancedSettings({
                    ...advancedSettings,
                    isCaseSensitive: !advancedSettings.isCaseSensitive,
                  })
                }
              />
            </div>
          </InputField>
          <ApplyOnDropdown
            applyOn={applyOn as RuleApplyOnType}
            setApplyOn={(selectedOption) => {
              setApplyOn(selectedOption);
              setAdvancedSettings({
                ...advancedSettings,
                applyOnUrls: updateApplyRuleOnUrlList(
                  selectedOption,
                  decodedUrl
                ),
              });
            }}
            currentPageUrl={decodedUrl}
          />
        </div>
      )}
    </div>
  );
};
